// src/App.js
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { authApi } from './axiosInstance';
import { ensureMentoringSession } from './lib/session';
import ensureAuthSession from './utils/ensureAuthSession';

// Layout und Seiten importieren
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import MenteesPage from './pages/MenteesPage';
import MentorsPage from './pages/MentorsPage';
import LeadersPage from './pages/LeadersPage';
import OnboardingPage from './pages/OnboardingPage';
import ProfilePage from './pages/ProfilePage';
import LogoutPage from './pages/LogoutPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SuperadminDashboard from './pages/SuperadminDashboard';
import HrDashboard from './pages/HrDashboard';
import HrProfilePage from './pages/HrProfilePage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import NewSurveyPage from './pages/NewSurveyPage';
import EmployeeSurveyPage from './pages/EmployeeSurveyPage';
import MentoringInvitePage from './pages/MentoringInvitePage';
import OnboardingMenteePage from './pages/OnboardingMenteePage';
import MenteeProfilePage from './pages/MenteeProfilePage';
import MenteeDashboard from './pages/MenteeDashboard';
import OnboardingMentorPage from './pages/OnboardingMentorPage';
import MentorProfilePage from './pages/MentorProfilePage';
import OnboardingHRPage from './pages/OnboardingHRPage';
import MentorDashboard from './pages/MentorDashboard';
import MatchingResultPage from './components/MatchingResultPage';
import ProtectedRoute from './components/ProtectedRoute';
import HrOverviewPage from './pages/HrOverviewPage';
import HrMenteesPage from './pages/HrMenteesPage';
import HrMentorsPage from './pages/HrMentorsPage';
import ConfirmedPairsPage from './pages/ConfirmedPairsPage';
import HrMenteeAlertSettings from './components/hr/HrMenteeAlertSettings';
import FeedbackPage from './pages/Feedback/FeedbackPage';
import TestLinks from './pages/TestLinks';
import FeedbackLinkGenerator from './components/FeedbackLinkGenerator';
import ChatPopup from './components/ChatPopup';
import HrUpgradePage from './pages/HrUpgradePage';
import SupportChat from './components/SupportChat';
import ContactPage from './pages/ContactPage';
import CallEndedPage from "./pages/CallEndedPage";
import RequireAuth from './components/RequireAuth';
import { PairingProvider } from './context/PairingContext';
import ImpressumPage from './pages/ImpressumPage';
import DatenschutzPage from './pages/DatenschutzPage';
import SiteFooter from './components/SiteFooter';

// MPI-Feature-Flag: MPI-Seiten nur laden wenn aktiviert (nach allen imports)
// eslint-disable-next-line import/first
const MPI_ENABLED = process.env.REACT_APP_ENABLE_MPI === 'true';
// Ungated: /nl-login muss auch in einem unflagged Build (MPI=false) erreichbar
// bleiben (Logout-Ziel, alte Links/Bookmarks). NLLoginPage hat keine MPI-Abhaengigkeit.
const NLLoginPage = require('./pages/NLLoginPage').default;
const MALoginPage = MPI_ENABLED ? require('./pages/MALoginPage').default : null;
const MADashboardPage = MPI_ENABLED ? require('./pages/MADashboardPage').default : null;
const MPIDashboardPage = MPI_ENABLED ? require('./pages/MPIDashboardPage').default : null;
const RegionalDashboardPage = MPI_ENABLED ? require('./pages/RegionalDashboardPage').default : null;
const InviteEmployeesPage = MPI_ENABLED ? require('./pages/InviteEmployeesPage').default : null;
const MARegisterPage = MPI_ENABLED ? require('./pages/MARegisterPage').default : null;

function RequireRole({ user, loadingUser, allow, children }) {
  const location = useLocation();

  if (loadingUser) return null;

  const role = user?.role || user?.mentoring_role;

  if (!role) {
    return <Navigate to="/login" replace />;
  }

  if (!allow.includes(role)) {
    const q = location.search || '';

    const fallback =
      role === 'mentor' ? `/mentor-dashboard${q}` :
      role === 'mentee' ? `/mentee-dashboard${q}` :
      (role === 'hr' || role === 'hr_admin' || role === 'hr_user') ? `/hr/overview${q}` :
      role === 'superadmin' ? `/superadmin${q}` :
      `/login${q}`;

    return <Navigate to={fallback} replace />;
  }

  return children;
}

function App() {
  const [csrfToken, setCsrfToken] = useState('');
  const [user, _setUser] = useState(null);

  const setUser = (v) => {
    console.log('[setUser] typeof:', typeof v, 'value:', v);
    console.trace('[setUser trace]');
    _setUser(v);
  };
  const [loadingUser, setLoadingUser] = useState(true);

  // Verhindert, dass der Boot-Effekt einen parallelen Login-Erfolg mit null überschreibt
  const loginSucceededRef = React.useRef(false);

  // 🚦 Gate: UI erst rendern, wenn SSO sicher steht
  const [bootReady, setBootReady] = useState(false);

  const [chatConfig, setChatConfig] = useState(null);
  const [isSupportOpen, setSupportOpen] = useState(false);
  // MPI-Seiten-spezifische Overrides für SupportChat (role, firstName, tenantName)
  const [supportOverrides, setSupportOverrides] = useState(null);

  useEffect(() => {
    window.__APP_USER__ = user;
    window.__APP_LOADING__ = loadingUser;
    window.__APP_BOOT_READY__ = bootReady;
  }, [user, loadingUser, bootReady]);

  // ---- HashRouter-Schutz ----
  useEffect(() => {
    const path = window.location.pathname || '/';
    const hash = window.location.hash || '';
    if (!hash && (path === '/kontakt' || path === '/contact')) {
      window.location.replace(`/#${path}`);
    }
  }, []);

  console.log('APP user:', window.__APP_USER__);
  console.log('APP role:', window.__APP_USER__?.role);
  console.log('APP loading/boot:', window.__APP_LOADING__, window.__APP_BOOT_READY__);
  console.log('HASH:', window.location.hash);

  // -- Hilfsfunktionen für „attach & verify“ (bewusst mit fetch, ohne Interceptor) --
  async function verifyMentoringSession() {
    try {
      const r = await fetch('/mentoring/whoami', { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      return Boolean(j && (j.ok || j.user));
    } catch {
      return false;
    }
  }

  function buildAttachPayload(who) {
    const u = who?.user ?? {};
    return {
      user: {
        id:        u.id ?? u.user_id ?? null,
        user_id:   u.user_id ?? u.id ?? null,
        email:     u.email ?? null,
        role:      u.role ?? null,
        firstName: u.firstName ?? u.first_name ?? null,
        lastName:  u.lastName ?? u.last_name ?? null,
        client_id: u.client_id ?? u.customer_id ?? u.clientId ?? null,
      }
    };
  }

  async function hardAttachFromWho(who) {
    try {
      const payload = buildAttachPayload(who);
      const r = await fetch('/mentoring/sso-attach', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return r.ok;
    } catch {
      return false;
    }
  }

  // 🟢 Startup: AUTH-Session sicherstellen, CSRF laden, dann UI freigeben
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const ok = await ensureAuthSession();
        console.log('[BOOT] ensureAuthSession returned:', ok, 'type:', typeof ok);

        if (ok) {
          const r = await fetch('/auth/whoami', { credentials: 'include' });
          const j = await r.json().catch(() => ({}));
          let resolvedUser = j?.user ?? null;

          // Immer /mentoring/whoami holen und mergen — liefert role, first_name,
          // tenant_name etc. die in /auth/whoami oft fehlen (NL-Login-User)
          try {
            const mr = await fetch('/mentoring/whoami', { credentials: 'include' });
            if (mr.ok) {
              const md = await mr.json().catch(() => ({}));
              const mu = md?.user || (md?.ok ? md : null);
              if (mu && (mu.role || mu.first_name || mu.firstName)) {
                resolvedUser = { ...resolvedUser, ...mu };
              }
            }
          } catch { /* ignore */ }

          // Normalize: ensure both camelCase and snake_case fields exist
          if (resolvedUser) {
            resolvedUser.first_name = resolvedUser.first_name || resolvedUser.firstName || resolvedUser.firstname || '';
            resolvedUser.firstName = resolvedUser.firstName || resolvedUser.first_name || '';
            resolvedUser.role = resolvedUser.role || resolvedUser.user_role || '';
          }
          if (alive) setUser(resolvedUser);
        } else {
          // Ref-Guard: kein null schreiben, wenn handleLogin parallel erfolgreich war.
          if (alive && !loginSucceededRef.current) setUser(null);
        }

        try {
          const { data } = await authApi.get('/csrf-token', { withCredentials: true });
          if (alive) setCsrfToken(data?.csrfToken || '');
        } catch (err) {
          console.error('CSRF fehlgeschlagen:', err);
        }
      } finally {
        if (alive) {
          setLoadingUser(false);
          setBootReady(true);
        }
      }
    })();

    return () => { alive = false; };
  }, []);

  // KI-Assistent: MPI-Seiten dispatchen dieses Event mit role/firstName/tenantName
  useEffect(() => {
    const handleOpenSupport = (e) => {
      const detail = e?.detail || {};
      const overrides = {};
      if (detail.role) {
        overrides.role = detail.role;
        overrides.firstName = detail.firstName || '';
        overrides.tenantName = detail.tenantName || '';
      }
      // tenantId immer aus dem aktuellen Dispatch (auch leer) -> kein stale-Tenant,
      // auch fuer rollenlose Dispatches (Pulse "Massnahme anlegen").
      overrides.tenantId = detail.tenantId || '';
      if (detail.message) {
        overrides.prefillMessage = detail.message;
      }
      if (Object.keys(overrides).length) setSupportOverrides(prev => ({ ...prev, ...overrides }));
      setSupportOpen(true);
    };
    window.addEventListener('open-support-chat', handleOpenSupport);
    return () => window.removeEventListener('open-support-chat', handleOpenSupport);
  }, []);

  // Chat-Event-Listener
  useEffect(() => {
    const handleChatPopup = (e) => {
      const d = e?.detail || {};

      const menteeRaw = d.menteeId ?? d?.context?.menteeId;
      const mentorRaw = d.mentorId ?? d?.context?.mentorId;

      const menteeIdNum = Number.isFinite(Number(menteeRaw)) ? Number(menteeRaw) : null;
      const mentorIdNum = Number.isFinite(Number(mentorRaw)) ? Number(mentorRaw) : null;

      const receiverRole = d.receiverRole || 'mentee';
      const senderRole = d.senderRole || user?.role || 'hr';

      // neues Flag: bestimmte HR→Mentor Events dürfen von der App behandelt werden
      const forceAppHandling =
        d.forceAppHandling || d?.context?.forceAppHandling;

      // HR → Mentor wird standardmäßig lokal gehandhabt.
      // Nur wenn forceAppHandling=true, lässt die App das Event durch.
      if (senderRole === 'hr' && receiverRole === 'mentor' && !forceAppHandling) {
        console.info(
          '[App] openChatPopup: HR→Mentor wird lokal gehandhabt, App ignoriert Event (ohne forceAppHandling).'
        );
        return;
      }

      if (!user) {
        window.__pendingChatPopupEvent__ = {
          ...d,
          menteeId: menteeIdNum,
          mentorId: mentorIdNum,
          receiverRole,
          senderRole,
        };
        return;
      }

      const needsMentee = receiverRole === 'mentee' || senderRole === 'mentee';
      const needsMentor = receiverRole === 'mentor' || senderRole === 'mentor';

      if ((needsMentee && menteeIdNum === null) || (needsMentor && mentorIdNum === null)) {
        console.warn('[App] openChatPopup: Ungültige IDs', {
          d,
          menteeIdNum,
          mentorIdNum,
          senderRole,
          receiverRole,
        });
        return;
      }

      if (menteeIdNum === null && mentorIdNum === null) {
        console.warn('[App] openChatPopup: weder menteeId noch mentorId gesetzt', d);
        return;
      }

      setChatConfig({
        menteeId: menteeIdNum,
        mentorId: mentorIdNum,
        senderRole,
        receiverRole,
        presetMessage: d.presetMessage || '',
      });
    };

    window.addEventListener('openChatPopup', handleChatPopup);
    return () => window.removeEventListener('openChatPopup', handleChatPopup);
  }, [user]);

  // Pending-Chat übernehmen
  useEffect(() => {
    if (user && window.__pendingChatPopupEvent__) {
      const d = window.__pendingChatPopupEvent__;
      window.__pendingChatPopupEvent__ = null;

      const menteeRaw = d.menteeId ?? d?.context?.menteeId;
      const mentorRaw = d.mentorId ?? d?.context?.mentorId;

      const menteeIdNum = Number.isFinite(Number(menteeRaw)) ? Number(menteeRaw) : null;
      const mentorIdNum = Number.isFinite(Number(mentorRaw)) ? Number(mentorRaw) : null;

      const receiverRole = d.receiverRole || 'mentee';
      const senderRole = d.senderRole || user?.role || 'hr';

      const forceAppHandling =
        d.forceAppHandling || d?.context?.forceAppHandling;

      // Auch hier: HR → Mentor nur ignorieren, wenn nicht explizit erlaubt
      if (senderRole === 'hr' && receiverRole === 'mentor' && !forceAppHandling) {
        console.info(
          '[App] pending openChatPopup: HR→Mentor wird lokal gehandhabt, App ignoriert Event (ohne forceAppHandling).'
        );
        return;
      }

      const needsMentee = receiverRole === 'mentee' || senderRole === 'mentee';
      const needsMentor = receiverRole === 'mentor' || senderRole === 'mentor';

      if ((needsMentee && menteeIdNum === null) || (needsMentor && mentorIdNum === null)) {
        console.warn('[App] pending openChatPopup: Ungültige IDs', {
          d,
          menteeIdNum,
          mentorIdNum,
          senderRole,
          receiverRole,
        });
        return;
      }

      if (menteeIdNum === null && mentorIdNum === null) {
        console.warn('[App] pending openChatPopup: weder menteeId noch mentorId gesetzt', d);
        return;
      }

      setChatConfig({
        menteeId: menteeIdNum,
        mentorId: mentorIdNum,
        senderRole,
        receiverRole,
        presetMessage: d.presetMessage || '',
      });
    }
  }, [user]);

  // Splash solange User lädt oder SSO noch nicht verifiziert
  if (loadingUser || !bootReady) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#000',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>Initialisiere Sitzung…</h3>
          <div style={{ opacity: 0.7, marginTop: 6, fontSize: 14 }}>
            (Login &amp; SSO werden gesichert)
          </div>
        </div>
      </div>
    );
  }

  return (
    <PairingProvider user={user}>
      <Routes>
        {/* Seiten ohne Layout */}
        <Route path="/feedback-form" element={<Navigate to="/feedback" replace />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/feedback-test" element={<FeedbackLinkGenerator />} />
        <Route path="/test-feedback" element={<TestLinks />} />

        {/* Hauptbereich mit Layout */}
        <Route
          element={
            <Layout
              user={user}
              setUser={setUser}
              onSupportClick={() => setSupportOpen(true)}
              onLogoutClick={() => {
                window.location.replace('/logout');
              }}
            />
          }
        >
          {/* Public */}
          <Route path="/" element={<HomePage />} />
          <Route path="/kontakt" element={<ContactPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />

          <Route
            path="/login"
            element={
              user
                ? (() => {
                    const role = String(user?.role || user?.mentoring_role || '').toLowerCase();

                    // Single Source: firstLogin + first/last (keine dept/job Pflicht)
                    const firstLogin = Boolean(
                      user?.is_first_login ?? user?.first_login ?? user?.firstLogin
                    );

                    const fn =
                      (user?.first_name ?? user?.firstName ?? user?.firstname ?? '') || '';
                    const ln =
                      (user?.last_name ?? user?.lastName ?? user?.lastname ?? '') || '';

                    const dept =
                      (user?.department ?? user?.abteilung ?? user?.dept ?? '') || '';
                    const job =
                      (user?.jobTitle ?? user?.job_title ?? user?.jobtitle ?? '') || '';

                    const profileMissing = !String(fn).trim() || !String(ln).trim();

                    const onboardingComplete = Boolean(user?.onboarding_complete ?? user?.onboardingComplete);
                    const needsOnboarding =
                      role === 'mentee' || role === 'mentor'
                        ? !onboardingComplete
                        : firstLogin;

                    // Wenn Mentee nur wegen fehlendem Profil hier landet: Passwort nicht nochmal erzwingen
                   try {
                      const existingSkip = sessionStorage.getItem('ONBOARDING_SKIP_PASSWORD') === '1';

                      if (role === 'mentee' && needsOnboarding) {
                        // Wenn der Flag bereits z.B. aus ResetPassword kommt, nicht wieder löschen.
                        if (profileMissing || existingSkip) {
                          sessionStorage.setItem('ONBOARDING_SKIP_PASSWORD', '1');
                        } else {
                          sessionStorage.removeItem('ONBOARDING_SKIP_PASSWORD');
                        }
                      } else {
                        sessionStorage.removeItem('ONBOARDING_SKIP_PASSWORD');
                      }
                    } catch {}

                    const target =
                      role === 'mentor'
                        ? (needsOnboarding
                            ? '/mentoring/onboarding-mentor?lang=de'
                            : '/mentor-dashboard?lang=de')
                        : role === 'mentee'
                        ? (needsOnboarding
                            ? '/onboarding-mentee?lang=de'
                            : '/mentee-dashboard?lang=de')
                        : (role === 'hr' || role === 'hr_admin' || role === 'hr_user')
                        ? (firstLogin
                            ? '/onboarding?lang=de'
                            : '/hr/overview?lang=de')
                        : role === 'superadmin'
                        ? '/superadmin?lang=de'
                        : role === 'mentixx'
                        ? '/superadmin?lang=de'
                        : role === 'regional_manager'
                        ? '/regional-dashboard'
                        : role === 'nl_manager'
                        ? '/mpi-dashboard'
                        : '/';

                    return <Navigate to={target} replace />;
                  })()
                : <LoginPage csrfToken={csrfToken} setUser={setUser} loginSucceededRef={loginSucceededRef} />
            }
          />

          {/* Ungated: bleibt auch ohne MPI-Flag erreichbar (Logout-Ziel, alte Links). */}
          <Route path="/nl-login" element={<NLLoginPage />} />
          {MPI_ENABLED && MALoginPage && (
            <Route path="/ma-login" element={<MALoginPage />} />
          )}
          {MPI_ENABLED && MADashboardPage && (
            <Route path="/ma-dashboard" element={<MADashboardPage />} />
          )}
          {MPI_ENABLED && InviteEmployeesPage && (
            <Route path="/invite-employees" element={<InviteEmployeesPage />} />
          )}
          {MPI_ENABLED && MARegisterPage && (
            <Route path="/ma-register" element={<MARegisterPage />} />
          )}

          <Route path="/mentoring/register" element={<RegisterPage csrfToken={csrfToken} />} />
          <Route path="/register" element={<RegisterPage csrfToken={csrfToken} />} />
          <Route path="/register-invite" element={<RegisterPage csrfToken={csrfToken} />} />

          <Route
            path="/forgot-password"
            element={<ForgotPasswordPage csrfToken={csrfToken} />}
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage csrfToken={csrfToken} />}
          />

          {/* Logout NICHT schützen */}
          <Route path="/logout" element={<LogoutPage onUserChange={setUser} />} />

          {/* Protected */}
          <Route element={<RequireAuth user={user} loadingUser={loadingUser} />}>
            <Route path="/mentees" element={<MenteesPage />} />
            <Route path="/mentors" element={<MentorsPage />} />
            <Route path="/leaders" element={<LeadersPage />} />
            <Route path="/onboarding" element={<OnboardingPage csrfToken={csrfToken} />} />
            <Route path="/profile" element={<ProfilePage />} />

            <Route
              path="/onboarding-mentee"
              element={<OnboardingMenteePage csrfToken={csrfToken} />}
            />

            <Route
              path="/mentoring/onboarding-mentor"
              element={<OnboardingMentorPage csrfToken={csrfToken} />}
            />
            <Route
              path="/onboarding-mentor"
              element={<OnboardingMentorPage csrfToken={csrfToken} />}
            />

            <Route path="/mentor-profile" element={<MentorProfilePage />} />
            <Route
              path="/mentor-dashboard"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['mentor']}>
                  <MentorDashboard />
                </RequireRole>
              }
            />

            <Route path="/superadmin" element={<SuperadminDashboard />} />

            {/* HR-Routen (Role-Guard) */}
            <Route
              path="/hr"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['hr', 'hr_admin']}>
                  <HrDashboard />
                </RequireRole>
              }
            />

            <Route
              path="/hr-profile"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['hr', 'hr_admin', 'hr_user']}>
                  <HrProfilePage csrfToken={csrfToken} />
                </RequireRole>
              }
            />

            <Route
              path="/change-password"
              element={<ChangePasswordPage csrfToken={csrfToken} />}
            />

            <Route
              path="/hr/new-survey"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['hr', 'hr_admin']}>
                  <NewSurveyPage user={user} />
                </RequireRole>
              }
            />

            <Route
              path="/hr/mentoring-invite"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['hr', 'hr_admin']}>
                  <MentoringInvitePage />
                </RequireRole>
              }
            />

            <Route
              path="/hr/invitations"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['hr', 'hr_admin']}>
                  <MentoringInvitePage />
                </RequireRole>
              }
            />

            <Route
              path="/mentee-profile"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['mentee']}>
                  <MenteeProfilePage />
                </RequireRole>
              }
            />

            <Route path="/mentee-dashboard" element={<MenteeDashboard />} />
            <Route path="/call-ended" element={<CallEndedPage user={user} />} />

            <Route
              path="/hr/overview"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['hr', 'hr_admin', 'hr_user']}>
                  <HrOverviewPage />
                </RequireRole>
              }
            />
            <Route
              path="/hr/mentees"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['hr', 'hr_admin', 'hr_user']}>
                  <HrMenteesPage />
                </RequireRole>
              }
            />
            <Route
              path="/hr/mentors"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['hr', 'hr_admin', 'hr_user']}>
                  <HrMentorsPage />
                </RequireRole>
              }
            />
            <Route
              path="/hr/confirmed-pairs"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['hr', 'hr_admin', 'hr_user']}>
                  <ConfirmedPairsPage />
                </RequireRole>
              }
            />
            <Route path="/onboarding-hr" element={<OnboardingHRPage />} />

            <Route
              path="/hr/upgrade"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['hr']}>
                  <HrUpgradePage />
                </RequireRole>
              }
            />

            <Route
              path="/hr/notifications"
              element={
                <RequireRole user={user} loadingUser={loadingUser} allow={['hr', 'hr_admin', 'hr_user']}>
                  <HrMenteeAlertSettings />
                </RequireRole>
              }
            />

            {MPI_ENABLED && MPIDashboardPage && (
              <Route
                path="/mpi-dashboard"
                element={<MPIDashboardPage />}
              />
            )}
            {MPI_ENABLED && RegionalDashboardPage && (
              <Route
                path="/regional-dashboard"
                element={<RegionalDashboardPage />}
              />
            )}
          </Route>
        </Route>

        {/* Routen außerhalb des Layouts */}
        <Route path="/employee/survey/:id" element={<EmployeeSurveyPage />} />

        <Route
          path="/matching-result"
          element={
            <ProtectedRoute user={user} loadingUser={loadingUser}>
              <MatchingResultPage />
            </ProtectedRoute>
          }
        />
      </Routes>

      <SiteFooter />

      {chatConfig && (
        <ChatPopup
          menteeId={chatConfig.menteeId}
          mentorId={chatConfig.mentorId}
          senderRole={chatConfig.senderRole}
          receiverRole={chatConfig.receiverRole}
          presetMessage={chatConfig.presetMessage}
          onClose={() => setChatConfig(null)}
        />
      )}

      {(user?.email || supportOverrides?.role) && (
        <SupportChat
          open={isSupportOpen}
          onClose={() => { setSupportOpen(false); setSupportOverrides(null); }}
          role={supportOverrides?.role || user?.role || ''}
          firstName={supportOverrides?.firstName || user?.first_name || user?.firstName || ''}
          tenantName={supportOverrides?.tenantName || ''}
          userKey={user?.email || user?.user_id || user?.id || ''}
          prefillMessage={supportOverrides?.prefillMessage || ''}
          tenantId={supportOverrides?.tenantId || ''}
        />
      )}
    </PairingProvider>
  );
}

export default App;


